import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { toast } from 'react-toastify';
import { uploadFiles } from '../../../../helpers/file_manager/backend_helper';
import {
	addNewInspection,
	getInspectionListQuery,
	updateInspection,
} from '../../../../slices/inspection/thunk';
import { getInspectionTypeList } from '../../../../slices/inspectionType/thunk';
import { getInspectionAuthorityList } from '../../../../slices/inspectionAuthority/thunk';
import { getEstablishmentList } from '../../../../slices/establishment/thunk';
import { getPersonList, addNewPerson } from '../../../../slices/person/thunk';
import { getManagementPositionList, addNewManagementPosition } from '../../../../slices/managementPosition/thunk';
import { getFiscalizerList, addNewFiscalizer } from '../../../../slices/fiscalizer/thunk';
import { toStringId, getDataFiscalizacionForModal } from '../utils';

//Module-level selectors (created once, not recreated on every render)

const selectProfile = createSelector(
	state => state.Profile,
	profile => ({
		establishmentActive: profile.establishmentActive,
		user: profile.user,
	})
);

const selectFiscalizaciones = createSelector(
	state => state.InspectionType,
	state => state.InspectionAuthority,
	state => state.Establishments,
	state => state.Persons,
	state => state.ManagementPositions,
	state => state.Fiscalizer,
	(inspectionType, inspectionAuthority, establishments, persons, managementPositions, fiscalizer) => ({
		inspectionTypeListSelect: inspectionType?.inspectionTypeListSelect,
		inspectionAuthorityListSelect: inspectionAuthority?.inspectionAuthorityListSelect,
		establishmentList: establishments?.establishmentList,
		personList: persons?.personList,
		managementPositionList: managementPositions?.managementPositionList,
		fiscalizerListSelect: fiscalizer?.fiscalizerListSelect,
	})
);

//Hook

const useFiscalizacionesList = () => {
	const dispatch = useDispatch();

	const { establishmentActive, user: profileUser } = useSelector(selectProfile);
	const selectedEstablishmentId = establishmentActive?.item?._id || null;
	const isCentroRole = profileUser?.role_code === 'CTO';

	const {
		inspectionTypeListSelect,
		inspectionAuthorityListSelect,
		establishmentList,
		personList,
		managementPositionList,
		fiscalizerListSelect,
	} = useSelector(selectFiscalizaciones);

	const [dataFiscalizacion, setDataFiscalizacion] = useState({});
	const [modal, setModal] = useState(false);
	const [modalDelete, setModalDelete] = useState(false);
	const [modalIncumplimientos, setModalIncumplimientos] = useState(false);
	const [modalFiles, setModalFiles] = useState(false);
	const [isEdit, setIsEdit] = useState(false);
	const [refetchRemoteData, setRefetchRemoteData] = useState(0);
	const [refetchActividades, setRefetchActividades] = useState(0);

	// Bust backend cache then trigger refetch (mirrors the manual refresh button behavior)
	const handleClearCacheAndRefetch = useCallback(async () => {
		await dispatch(getInspectionListQuery({ clear_cache: true, pagination: { page: 1, pageSize: 1 } }));
		setRefetchRemoteData(prev => prev + 1);
	}, [dispatch]);

	useEffect(() => {
		dispatch(getInspectionTypeList());
		dispatch(getInspectionAuthorityList());
		dispatch(getEstablishmentList());
		dispatch(getPersonList());
		dispatch(getManagementPositionList());
		dispatch(getFiscalizerList());
	}, [dispatch]);

	//Options

	const establishmentOptions = useMemo(
		() =>
			(establishmentList || []).map(item => ({
				item,
				value: item._id,
				label: item.name,
			})),
		[establishmentList]
	);

	const personOptions = useMemo(
		() =>
			(personList || []).map(item => ({
				item,
				value: toStringId(item._id),
				label: item.name || '',
			})),
		[personList]
	);

	const managementPositionOptions = useMemo(
		() =>
			(managementPositionList || []).map(item => ({
				value: toStringId(item._id),
				label: item.name || '',
			})),
		[managementPositionList]
	);

	const fiscalizerOptions = useMemo(
		() => (fiscalizerListSelect || []).map(o => ({ value: toStringId(o.value), label: o.label || '' })),
		[fiscalizerListSelect]
	);

	//Filters

	const baseFilters = useMemo(() => {
		const filters = { disable: { $eq: false } };
		if (selectedEstablishmentId && selectedEstablishmentId !== 'all') {
			filters['establishment._id'] = { $eq: selectedEstablishmentId };
		}
		return filters;
	}, [selectedEstablishmentId]);

	//Handlers

	const handleToggleModal = useCallback(() => setModal(prev => !prev), []);
	const handleToggleModalDelete = useCallback(() => setModalDelete(prev => !prev), []);
	const handleToggleModalFiles = useCallback(() => setModalFiles(prev => !prev), []);

	const handleClickNew = useCallback(() => {
		setDataFiscalizacion({});
		setIsEdit(false);
		setModal(true);
	}, []);

	const handleClickEdit = useCallback(data => {
		setDataFiscalizacion(getDataFiscalizacionForModal(data));
		setIsEdit(true);
		setModal(true);
	}, []);

	const handleClickDelete = useCallback(data => {
		setDataFiscalizacion(getDataFiscalizacionForModal(data));
		setModalDelete(true);
	}, []);

	const handleClickIncumplimientos = useCallback(data => {
		setDataFiscalizacion(getDataFiscalizacionForModal(data));
		setModalIncumplimientos(true);
	}, []);

	const handleClickFile = useCallback(data => {
		setDataFiscalizacion(getDataFiscalizacionForModal(data));
		setModalFiles(true);
	}, []);

	const handleDeleteFiscalizacionSuccess = useCallback(() => {
		setRefetchRemoteData(prev => prev + 1);
	}, []);

	const handleDataChange = useCallback(() => {
		setRefetchRemoteData(prev => prev + 1);
		setRefetchActividades(prev => prev + 1);
	}, []);

	//Save handler

	const handleSave = async values => {
		try {
			let fileId = null;
			const selectedAuthorities = (values.entidades || []).map(toStringId).filter(Boolean);

			if (values.documento && values.documento instanceof File) {
				const formData = new FormData();
				formData.append('files', values.documento);

				const uploadResponse = await uploadFiles({ formData });

				if (!uploadResponse || !uploadResponse.length || !uploadResponse[0]._id) {
					toast.error('Error al subir el archivo');
					return;
				}

				fileId = uploadResponse[0]._id;
			} else if (values.documento_id) {
				fileId = values.documento_id;
			}

			let closureFileId = null;
			if (values.finding_closure_document && values.finding_closure_document instanceof File) {
				const formData = new FormData();
				formData.append('files', values.finding_closure_document);

				const uploadResponse = await uploadFiles({ formData });

				if (!uploadResponse || !uploadResponse.length || !uploadResponse[0]._id) {
					toast.error('Error al subir el respaldo de cierre de hallazgo');
					return;
				}

				closureFileId = uploadResponse[0]._id;
			} else if (values.finding_closure_file_id) {
				closureFileId = values.finding_closure_file_id;
			}

			if (!selectedAuthorities.length) {
				toast.error('Debe seleccionar al menos una entidad fiscalizadora.');
				return;
			}

			//Resolve responsible_person_id
			const isExistingPerson = personOptions.some(p => p.value === values.responsable_id);
			let responsiblePersonId = values.responsable_id;
			let resolvedManagementPositionId = values.management_position_id;

			if (!isExistingPerson && values.responsable_id) {
				// New person resolve position first if needed
				const isExistingPosition = managementPositionOptions.some(
					p => p.value === values.management_position_id
				);
				let positionId = values.management_position_id;

				if (!isExistingPosition && values.management_position_id) {
					const posResult = await dispatch(
						addNewManagementPosition({ name: values.management_position_id })
					);
					if (posResult.type.endsWith('/rejected')) {
						toast.error('Error al crear el cargo.');
						return;
					}
					positionId = posResult.payload?._id || posResult.payload?.data?._id || '';
					if (!positionId) {
						toast.error('Error al obtener el ID del cargo creado.');
						return;
					}
				}

				resolvedManagementPositionId = positionId;

				const personResult = await dispatch(
					addNewPerson({
						name: values.responsable_id,
						...(positionId ? { management_position_id: positionId } : {}),
					})
				);
				if (personResult.type.endsWith('/rejected')) {
					toast.error('Error al crear la persona responsable.');
					return;
				}
				responsiblePersonId =
					personResult.payload?._id || personResult.payload?.data?._id || '';
				if (!responsiblePersonId) {
					toast.error('Error al obtener el ID de la persona creada.');
					return;
				}
			}

			//Resolve fiscalizer IDs
			const resolvedFiscalizadorIds = [];
			const fiscalizadorNameById = {};
			for (const fiscId of values.fiscalizadores || []) {
				if (!fiscId) continue;
				const existing = fiscalizerOptions.find(o => o.value === fiscId);
				if (existing) {
					resolvedFiscalizadorIds.push(fiscId);
					fiscalizadorNameById[fiscId] = existing.label;
				} else {
					// New fiscalizador name — create in master and get its ID
					const result = await dispatch(addNewFiscalizer({ name: fiscId }));
					if (result.type.endsWith('/rejected')) {
						toast.error(`Error al crear fiscalizador "${fiscId}".`);
						return;
					}
					const newId = result.payload?._id || result.payload?.data?._id || '';
					if (!newId) {
						toast.error('Error al obtener el ID del fiscalizador creado.');
						return;
					}
					resolvedFiscalizadorIds.push(newId);
					fiscalizadorNameById[newId] = result.payload?.name || fiscId;
				}
			}

			// Compute display names for backwards compatibility
			const fiscalizadorNames = resolvedFiscalizadorIds.map(
				fiscId => fiscalizadorNameById[fiscId] || fiscId
			);

			const payload = {
				inspection_date: values.fecha || null,
				establishment_id: values.establecimiento_id || '',
				inspection_type_id: values.tipo || '',
				inspection_authority_ids: selectedAuthorities,
				inspection_authority_id: selectedAuthorities[0] || '',
				responsible_person_id: responsiblePersonId || '',
				role_user_id: resolvedManagementPositionId || '',
				fiscalizer_ids: resolvedFiscalizadorIds,
				fiscalizer_names: fiscalizadorNames,
				file_id: fileId,
				finding_closure_file_id: closureFileId,
				observation: values.observaciones || '',
				review_notes: values.review_notes || null,
				waiting_for_observations: !!values.waiting_for_observations,
				...(values.inspection_status_id ? { inspection_status_id: values.inspection_status_id } : {}),
			};

			const resultAction = isEdit
				? await dispatch(updateInspection({ ...payload, _id: dataFiscalizacion._id }))
				: await dispatch(addNewInspection(payload));

			if (resultAction.type.endsWith('/rejected')) {
				toast.error('Error al guardar la fiscalización.');
				return;
			}

			setRefetchRemoteData(prev => prev + 1);
			setModal(false);
		} catch (error) {
			toast.error('Error al guardar la fiscalización');
		}
	};

	return {
		dataFiscalizacion,
		modal,
		modalDelete,
		modalIncumplimientos,
		modalFiles,
		isEdit,
		refetchRemoteData,
		refetchActividades,
		isCentroRole,
		inspectionTypeListSelect,
		inspectionAuthorityListSelect,
		establishmentOptions,
		personOptions,
		managementPositionOptions,
		fiscalizerOptions,
		baseFilters,
		handleClearCacheAndRefetch,
		handleToggleModal,
		handleToggleModalDelete,
		handleToggleModalFiles,
		handleClickNew,
		handleClickEdit,
		handleClickDelete,
		handleClickIncumplimientos,
		handleClickFile,
		handleDeleteFiscalizacionSuccess,
		handleDataChange,
		handleSave,
		setModalIncumplimientos,
	};
};

export default useFiscalizacionesList;
